import { ProDocLibrary, entriesOf, libraryQueryFromParams, libraryQueryToParams, type LibraryQuery } from '@fiveox/prodoc-ui'
import { Alert, Button, Dialog, Input, PageHeader, useToast } from '@fiveox/ui'
import { Bookmark, CloudUpload } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import { useFolderMenu } from '../components/folder-menu'
import { useOrg } from '../components/org-context'
import { useSharingHost } from '../components/sharing-host'
import { useUpload, type UploadAt } from '../components/upload-provider'
import { DOCUMENT } from '../lib/access'
import { SERVICE_API } from '../lib/config'
import { saveFilter } from '../lib/goto'
import { session } from '../lib/session'

/**
 * Documents — the register. A thin host (ADR-0010): the screen is
 * `<ProDocLibrary>`, which takes the shell's fetcher and the selected org and
 * knows nothing of this app. What the route adds is **the URL**: every control
 * lives in it (`libraryQueryToParams`), so a filtered view is a link —
 * Overview's tiles, Go To's views and a folder in the tree all link in — and
 * **a saved filter is that link with a name** (FR-17, D32), kept per person and
 * organisation in this browser and listed in Go To.
 *
 * **W22 (FR-59, D57): upload is a popup over the register.** *Upload
 * documents* opens it into the workspace and folder being browsed; `/upload`
 * lands here as `?upload=1`, which opens it once and leaves the address; and
 * **files dropped anywhere on the screen** open it with them added, while it is
 * closed — once it is open, the uploader's own drop target takes every drop.
 *
 * **W25 (FR-60, D76): the folders are in the left menu.** The register is
 * `browse="menu"` — no left panel and no *By app* tab (the Filters panel's *Every
 * app* does it), so the table spans the width. The menu's tree, under
 * Documents, chooses the place through this address (`components/folder-menu.tsx`);
 * after its dialogs change a folder the register reads its folders again
 * (`folderRevision`), and after a bulk move here the tree does (`onFolderChange`).
 */
export function DocumentsPage() {
  const { org, can, userId } = useOrg()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const [params, setParams] = useSearchParams()
  const [saving, setSaving] = useState(false)
  const sharingHost = useSharingHost()
  const folders = useFolderMenu()

  const query = useMemo(() => fromParams(params), [params])
  const setQuery = (next: LibraryQuery) => setParams(toParams(next), { replace: true })
  const { openUpload, isOpen, canUpload } = useUpload()
  // Into the folder being browsed (FR-59); the popup's pickers still offer the others.
  const here: UploadAt = { workspaceId: query.workspace, folderId: query.folder }
  const dragging = useDropToUpload(canUpload && can(DOCUMENT, 'can_create') && !isOpen, (dropped) => openUpload({ ...here, ...dropped }))

  // `/upload` → `/documents?upload=1`: the popup opens once, and the address forgets it.
  const asked = params.get('upload') === '1'
  useEffect(() => {
    if (!asked) return
    if (can(DOCUMENT, 'can_create')) openUpload(here)
    const next = new URLSearchParams(params)
    next.delete('upload')
    setParams(next, { replace: true })
    // Once per arrival: the address is the trigger, and it is dropped here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asked])

  const upload = can(DOCUMENT, 'can_create') && (
    <Button onClick={() => openUpload(here)} aria-haspopup="dialog">
      <CloudUpload size={18} strokeWidth={1.75} aria-hidden />
      Upload documents
    </Button>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documents"
        actions={
          <>
            {location.search && !asked && (
              <Button variant="outline" onClick={() => setSaving(true)}>
                <Bookmark size={16} aria-hidden />
                Save this view
              </Button>
            )}
            {upload}
          </>
        }
      />
      {dragging && (
        <Alert tone="info">
          Drop the files to upload them {query.folder ? 'into this folder' : query.workspace ? 'into this workspace' : 'here'} — the upload opens with them.
        </Alert>
      )}
      <ProDocLibrary
        apiUrl={SERVICE_API}
        fetcher={session.authed}
        orgId={org.id}
        userId={userId}
        {...sharingHost}
        query={query}
        onQueryChange={setQuery}
        // W25 (FR-60): the places are chosen in the left menu, so the table has the width.
        browse="menu"
        folderRevision={folders?.revision}
        onFolderChange={folders?.refresh}
        onOpen={(doc) => navigate(`/documents/${doc.id}`)}
        emptyAction={
          can(DOCUMENT, 'can_create') && (
            <Button variant="outline" onClick={() => openUpload(here)} aria-haspopup="dialog">
              <CloudUpload size={16} strokeWidth={1.75} aria-hidden />
              Upload documents
            </Button>
          )
        }
      />
      <SaveViewDialog
        open={saving}
        onClose={() => setSaving(false)}
        onSave={(name) => {
          // The page and its size are where the reader was, not what the view is.
          const saved = toParams({ ...query, page: 1 })
          saveFilter(userId, org.id, { name, to: `/documents${saved.toString() ? `?${saved}` : ''}` })
          setSaving(false)
          toast(`Saved “${name}”. Open it from Go To (Alt+G).`)
        }}
      />
    </div>
  )
}

function SaveViewDialog({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (name: string) => void }) {
  const [name, setName] = useState('')
  const trimmed = name.trim()
  return (
    <Dialog open={open} onClose={onClose} title="Save this view">
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault()
          if (trimmed) {
            onSave(trimmed.slice(0, 80))
            setName('')
          }
        }}
      >
        <p className="text-muted-foreground text-sm">
          Every filter, the folder and the sort, under a name — kept for you in this organisation, in this browser, and opened from Go To.
        </p>
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Invoices awaiting review" maxLength={80} autoFocus />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!trimmed}>
            Save view
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

/* ------------------------------------------------------- drop to upload */

/** Whether a drag carries files — a dragged link or a text selection is not an upload. */
const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')

/**
 * Files or a folder dragged onto the screen while `enabled` (FR-59): the
 * window accepts them, says so while they hover (`true`), and hands the drop to
 * `onDrop` — the files, or, when a folder is among them, the drop's entries
 * (`entriesOf`, which must be read inside the drop event), so the popup adds
 * the folder with its subfolders as its own drop area does (FR-7).
 */
export function useDropToUpload(enabled: boolean, onDrop: (dropped: { files?: File[]; entries?: FileSystemEntry[] }) => void): boolean {
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)
  const latest = useRef(onDrop)
  latest.current = onDrop

  useEffect(() => {
    if (!enabled) {
      depth.current = 0
      setDragging(false)
      return
    }
    const enter = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      depth.current += 1
      setDragging(true)
    }
    const over = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      // Without this the browser opens the file in place of the app.
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    }
    const leave = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setDragging(false)
    }
    const drop = (e: DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      depth.current = 0
      setDragging(false)
      // The entries are gone once the event returns, so they are taken now; a directory among them makes it a folder drop.
      const entries = entriesOf(e.dataTransfer)
      if (entries.some((entry) => entry.isDirectory)) latest.current({ entries })
      else latest.current({ files: Array.from(e.dataTransfer?.files ?? []) })
    }
    window.addEventListener('dragenter', enter)
    window.addEventListener('dragover', over)
    window.addEventListener('dragleave', leave)
    window.addEventListener('drop', drop)
    return () => {
      window.removeEventListener('dragenter', enter)
      window.removeEventListener('dragover', over)
      window.removeEventListener('dragleave', leave)
      window.removeEventListener('drop', drop)
    }
  }, [enabled])

  return dragging
}

/* ---------------------------------------------------------- URL ↔ query */

/** The register's question from the address — the package's own codec, so every host spells a view the same way. */
export const fromParams = libraryQueryFromParams

/** Only what differs from the default goes in the address, so a plain `/documents` stays plain. */
export const toParams = libraryQueryToParams
